import type { Vec } from '@/lib/uploader/geometry'

import { NET, RIM } from '@/lib/uploader/geometry'
import { clamp, smoothstep } from '@/lib/uploader/math'

export interface NetPose {
    stretchAt: (v: number) => number
    swayAt: (v: number) => number
    bulge: number
    bulgeAt: number
    offset: Vec
}

const still = (): number => 0

export const restNetPose = (offset: Vec = { x: 0, y: 0 }): NetPose => ({
    stretchAt: still,
    swayAt: still,
    bulge: 0,
    bulgeAt: 0,
    offset
})

/*
 * The net is the front half of a tapered cylinder hanging from the rim. Strands run
 * diagonally both ways from seven points on the rim and reflect at the sides, which
 * makes the diamond mesh. u runs across the net (0 to 1), v runs down it (0 at the rim).
 */
const STARTS = [-2 / 6, -1 / 6, 0, 1 / 6, 2 / 6, 3 / 6, 4 / 6, 5 / 6]
const SLOPE = 0.5
const SAMPLES = 9

interface Strand {
    start: number
    direction: 1 | -1
    from: number
    to: number
    samples: number
}

export const NET_STRANDS: Array<Strand> = ([1, -1] as const).flatMap(direction => STARTS.map(start => {
    const from = Math.max(0, -start / SLOPE)
    const to = Math.min(1, (1 - start) / SLOPE)

    return {
        start,
        direction,
        from,
        to,
        samples: Math.max(3, Math.round(SAMPLES * (to - from)) + 1)
    }
}))

const netPoint = (u: number, v: number, pose: NetPose): Vec => {
    const taper = NET.taper + (1 - NET.taper) * Math.pow(1 - v, 1.4)
    const swell = pose.bulge === 0 ? 0 : pose.bulge * Math.exp(-Math.pow((v - pose.bulgeAt) / 0.2, 2))
    const radius = RIM.rx * taper + swell
    const angle = (u - 0.5) * Math.PI
    const follow = 1 - 0.6 * v

    return {
        x: RIM.cx + Math.sin(angle) * radius + pose.swayAt(v) + pose.offset.x * follow,
        y: RIM.cy + v * NET.height + NET.height * pose.stretchAt(v) * Math.pow(v, 1.15) + Math.cos(angle) * radius * (RIM.ry / RIM.rx) + pose.offset.y * follow
    }
}

export const netPaths = (pose: NetPose): Array<string> => NET_STRANDS.map(strand => {
    let path = ''

    for (let index = 0; index < strand.samples; index++) {
        const v = strand.from + (strand.to - strand.from) * (index / (strand.samples - 1))
        const u = strand.direction === 1 ? strand.start + SLOPE * v : 1 - strand.start - SLOPE * v
        const point = netPoint(u, v, pose)

        path += `${index === 0 ? 'M' : 'L'}${point.x.toFixed(1)} ${point.y.toFixed(1)}`
    }

    return path
})

export const REST_NET_PATHS = netPaths(restNetPose())

const SETTLE_SECONDS = 1.1

/*
 * While a file passes through, the net stretches down and swells around it. When the
 * file slips out, the net recoils past rest and sways to a stop over about 700ms, with
 * the bottom lagging the top so the motion reads as cloth rather than rubber.
 */
export class NetMotion {
    private mode: 'rest' | 'track' | 'settle' = 'rest'
    private stretch = 0
    private bulge = 0
    private bulgeAt = 0
    private goal = { stretch: 0, bulge: 0, at: 0 }
    private settleStart = 0
    private settle = { stretch: 0, sway: 0, bulge: 0, at: 0 }
    private lastTime = 0

    track(fileY: number): void {
        const at = (fileY - RIM.cy) / NET.height

        this.mode = 'track'
        this.goal = {
            stretch: 0.26 * smoothstep(0.1, 0.95, at),
            bulge: 5 * smoothstep(-0.3, 0.1, at) * (1 - smoothstep(0.95, 1.3, at)),
            at
        }
    }

    release(push: number, now: number): void {
        this.mode = 'settle'
        this.settleStart = now
        this.settle = {
            stretch: this.stretch,
            sway: clamp(push / 45, -3.5, 3.5) + (Math.random() - 0.5) * 2,
            bulge: this.bulge,
            at: this.bulgeAt
        }
    }

    isActive(now: number): boolean {
        if (this.mode === 'track') return true

        return this.mode === 'settle' && now - this.settleStart < SETTLE_SECONDS * 1000
    }

    pose(now: number, offset: Vec): NetPose {
        const dt = clamp((now - this.lastTime) / 1000, 0, 0.05)

        this.lastTime = now

        if (this.mode === 'track') {
            const follow = 1 - Math.exp(-dt / 0.035)

            this.stretch += (this.goal.stretch - this.stretch) * follow
            this.bulge += (this.goal.bulge - this.bulge) * follow
            this.bulgeAt = this.goal.at

            const stretch = this.stretch

            return {
                stretchAt: () => stretch,
                swayAt: still,
                bulge: this.bulge,
                bulgeAt: this.bulgeAt,
                offset
            }
        }

        if (this.mode === 'settle') {
            const t = (now - this.settleStart) / 1000

            if (t >= SETTLE_SECONDS) {
                this.mode = 'rest'
                this.stretch = 0
                this.bulge = 0

                return restNetPose(offset)
            }

            const { stretch, sway, bulge, at } = this.settle
            const lagged = (v: number): number => Math.max(0, t - v * 0.05)

            this.stretch = stretch * Math.exp(-t / 0.17) * Math.cos(16.3 * t)
            this.bulge = bulge * Math.exp(-t / 0.06)
            this.bulgeAt = at + t * 2

            return {
                stretchAt: v => {
                    const local = lagged(v)

                    return stretch * Math.exp(-local / 0.17) * Math.cos(16.3 * local)
                },
                swayAt: v => {
                    const local = lagged(v)

                    return sway * Math.pow(v, 1.5) * Math.exp(-local / 0.2) * Math.sin(11.9 * local)
                },
                bulge: this.bulge,
                bulgeAt: this.bulgeAt,
                offset
            }
        }

        return restNetPose(offset)
    }
}
